// src/pages/TermsPrivacyPage.js
import React, { useState, useEffect, useRef } from 'react';
import styles from '../styles/StaticPages.module.css';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';

const TermsPrivacyPage = () => {
  const [activeTab, setActiveTab] = useState('terms');
  const location = useLocation();
  const pageTopRef = useRef(null);

  // Handle hash changes for direct linking to tabs
  useEffect(() => {
    // Get the hash without the # symbol
    const hash = location.hash.replace('#', '');
    
    // Set the active tab based on the hash if it's valid
    if (hash && ['terms', 'privacy', 'refund'].includes(hash)) {
      setActiveTab(hash);
      
      // Scroll to the top of the page
      window.scrollTo(0, 0);
      
      // Alternative: if you want to scroll to the page container instead
      if (pageTopRef.current) {
        pageTopRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location]);

  // Handle tab changes with URL hash updates
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    
    // Update the URL hash without full page reload
    window.history.pushState(null, '', `#${tab}`);
    
    // Scroll to the top of the page
    window.scrollTo(0, 0);
    
    // Alternative: scroll to the container element
    if (pageTopRef.current) {
      pageTopRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className={styles.container} ref={pageTopRef}>
      <Helmet>
        <title>
          {activeTab === 'terms' 
            ? 'Terms of Service' 
            : activeTab === 'privacy' 
              ? 'Privacy Policy' 
              : 'Refund Policy'} | MakeMeSongs
        </title>
        <meta name="description" content={
          activeTab === 'terms' 
            ? 'MakeMeSongs Terms of Service - Learn about our policies for custom song creation, licensing, and usage rights.'
            : activeTab === 'privacy' 
              ? 'MakeMeSongs Privacy Policy - Information on how we collect, use, and protect your personal data.'
              : 'MakeMeSongs Refund Policy - Information about our refund and credits policies for custom songs.'
        } />
      </Helmet>
      
      {/* Title outside the contentWrapper to match FAQPage */}
      <h1 className={styles.pageTitle}>
        {activeTab === 'terms' 
          ? 'Terms of Service' 
          : activeTab === 'privacy' 
            ? 'Privacy Policy' 
            : 'Refund & Credits Policy'}
      </h1>
      
      <div className={styles.contentWrapper}>
        <div className={styles.tabs}>
          <button 
            className={`${styles.tab} ${activeTab === 'terms' ? styles.activeTab : ''}`}
            onClick={() => handleTabChange('terms')}
            id="terms-tab"
          >
            Terms of Service
          </button>
          <button 
            className={`${styles.tab} ${activeTab === 'privacy' ? styles.activeTab : ''}`}
            onClick={() => handleTabChange('privacy')}
            id="privacy-tab"
          >
            Privacy Policy
          </button>
          <button 
            className={`${styles.tab} ${activeTab === 'refund' ? styles.activeTab : ''}`}
            onClick={() => handleTabChange('refund')}
            id="refund-tab"
          >
            Refund Policy
          </button>
        </div>
        
        <div className={styles.tabContent}>
          {activeTab === 'terms' ? (
            <div className={styles.termsContent}>
              <p className={styles.lastUpdated}>Last Updated: March 22, 2024</p>
              
              <section className={styles.termsSection}>
                <p>
                  MakeMeSongs.com ("MakeMeSongs," "we," "us," or "our") shall provide professional custom song creation services 
                  worldwide through its digital platform. These Terms of Service, Privacy Policy, and Refund Policy (collectively, 
                  the "Terms") shall govern access to and use of MakeMeSongs.com (the "Site") and related services. Legal entity 
                  formation shall be pending. By accessing or using the Site, each User shall agree to be bound by these Terms.
                </p>
              </section>
              
              <section className={styles.termsSection}>
                <h2>1. DEFINITIONS AND ELIGIBILITY</h2>
                
                <h3 className={styles.subSectionTitle}>1.1 Defined Terms</h3>
                <p>
                  The Service shall mean the custom song creation service provided by MakeMeSongs.<br />
                  The Musical Work shall mean any original song created through the Service.<br />
                  The Deliverables shall mean any and all materials created by the Service, including without limitation Musical Works, arrangements, recordings, mixes, and associated digital files.<br />
                  The User shall mean any person or entity using the Service.<br />
                  The User Content shall mean all information, stories, and preferences submitted for song creation.
                </p>
                
                <h3 className={styles.subSectionTitle}>1.2 User Eligibility</h3>
                <p>
                  Each User shall be at least eighteen (18) years of age or the legal age of majority in their jurisdiction. 
                  Each User accessing the Service from outside the United States shall be responsible for compliance with all 
                  applicable local laws. Each User creating an account shall warrant that they possess the legal authority to 
                  enter into these Terms.
                </p>
                
                <h3 className={styles.subSectionTitle}>1.3 Service Description</h3>
                <p>
                  The Service shall provide personalized musical composition services to Users worldwide through its digital platform. 
                  The Service shall create each Musical Work based on User-provided content and specifications. The Service shall 
                  deliver all works through secure digital means only. The Service shall not provide physical products at this time. 
                  The Service shall consider requests for commercial licensing on a case-by-case basis. The Service shall retain 
                  sole discretion regarding all creative and artistic decisions in the creation of Musical Works.
                </p>
                
                <h3 className={styles.subSectionTitle}>1.4 Account Management</h3>
                <p>
                  The Service shall require account creation for access to certain features. Each User shall provide and maintain 
                  accurate account information. Each User shall protect their account credentials. Each User shall notify the 
                  Service immediately of any unauthorized access. The Service shall retain the right to suspend or terminate any 
                  account for violations of these Terms.
                </p>
                
                <h3 className={styles.subSectionTitle}>1.5 Content License</h3>
                <p>
                  Each User shall grant to the Service a worldwide, royalty-free, perpetual, irrevocable, sublicensable, and 
                  transferable license to use submitted content. The Service shall use such content for: (i) Musical Work creation; 
                  (ii) service improvement; and (iii) promotional purposes. The Service shall have the right to create derivative 
                  promotional materials using anonymized lyrics or testimonials. Each User shall waive all moral rights in submitted 
                  content. The Service shall retain the right to reject or remove any content at its sole discretion.
                </p>
                
                <h3 className={styles.subSectionTitle}>1.6 Intellectual Property Rights</h3>
                <p>
                  The Service shall retain exclusive ownership of all Deliverables, including without limitation all songs, lyrics, 
                  recordings, and related creative materials. The Service shall grant each User a limited, non-exclusive, non-transferable, 
                  revocable license for personal, non-commercial use only. The Service shall prohibit unauthorized resale, remixing, 
                  commercial use, or misrepresentation of affiliation. Requests for commercial use or ownership transfers may be made 
                  by contacting support@mail.makemesongs.com. The Service shall protect its intellectual property rights under 
                  United States and international law.
                </p>
                
                <h3 className={styles.subSectionTitle}>1.7 Content Monitoring</h3>
                <p>
                  The Service shall retain the right to review, reject, or remove any content that violates these Terms. The Service 
                  shall prohibit content that is: (i) illegal, offensive, or harmful; (ii) harassing or discriminatory; (iii) infringing 
                  on intellectual property rights; or (iv) fraudulent or misleading. The Service shall retain sole discretion regarding 
                  refunds for rejected content.
                </p>
                
                <h3 className={styles.subSectionTitle}>1.8 Payment Terms and Processing</h3>
                <p>
                  The Service shall denominate all prices in United States Dollars. The Service shall require payment in full prior 
                  to order commencement. By submitting payment, User explicitly acknowledges and agrees that: (i) the Service will 
                  immediately begin allocation of creative resources; (ii) the work product is a custom-created digital good; 
                  (iii) all sales are final once production begins; and (iv) unauthorized payment reversals or credit card disputes 
                  may result in legal action. The Service shall maintain detailed records of User's explicit acceptance of these 
                  terms at checkout. The Service shall process payments through authorized third-party providers. The Service shall 
                  retain the right to cancel orders and pursue all available legal remedies for failed, reversed, or disputed payments. 
                  The Service shall assess additional fees for revisions outside original scope. The Service shall consider any attempt 
                  to reverse payment after work has begun as a material breach of these Terms. Each User shall be responsible for 
                  applicable taxes and duties.
                </p>
                
                <h3 className={styles.subSectionTitle}>1.9 Disclaimers and Limitations</h3>
                <p>
                  <strong>A. Disclaimers</strong><br />
                  The Service shall provide all Services and Deliverables "as is" and "as available." The Service shall disclaim 
                  all warranties, express or implied. The Service shall not guarantee satisfaction with any Musical Work.
                </p>
                <p>
                  <strong>B. Limitation of Liability</strong><br />
                  The Service shall not be liable for any indirect, incidental, special, or consequential damages. The Service's 
                  total liability shall not exceed the amount paid for the specific Musical Work in question.
                </p>
                
                <h3 className={styles.subSectionTitle}>1.10 Indemnification Obligations</h3>
                <p>
                  Each User shall defend, indemnify, and hold harmless the Service, its operators, affiliates, and contractors 
                  from any claims arising from: (i) User's use of the Services; (ii) User Content; (iii) violations of these Terms; 
                  or (iv) third-party claims related to User actions. The Service shall retain the right to control its legal defense.
                </p>
                
                <h3 className={styles.subSectionTitle}>1.11 Modification of Terms</h3>
                <p>
                  The Service shall retain the right to modify these Terms at any time. The Service shall post updated versions 
                  with a revised Last Updated date. Each User's continued use after modifications shall constitute acceptance. 
                  Each User shall be responsible for reviewing updates periodically.
                </p>
                
                <h3 className={styles.subSectionTitle}>1.12 Governing Law and Dispute Resolution</h3>
                <p>
                  <strong>A. Jurisdiction</strong><br />
                  These Terms shall be governed by the laws of the State of Florida, United States of America, without regard 
                  to conflict of law principles.
                </p>
                <p>
                  <strong>B. Arbitration Requirements</strong><br />
                  Each User residing in the United States shall submit to binding arbitration in Miami-Dade County, Florida. 
                  The American Arbitration Association or JAMS shall administer such arbitration. The Service shall permit 
                  virtual arbitration proceedings.
                </p>
                <p>
                  <strong>C. International Users</strong><br />
                  The Service shall preserve all mandatory consumer protection rights for Users outside the United States. 
                  The Service shall encourage good-faith dispute resolution through designated communication channels prior 
                  to formal proceedings.
                </p>
                <p>
                  <strong>D. Class Action Waiver</strong><br />
                  Each User shall waive any right to participate in class actions or jury trials to the extent permitted by law.
                </p>
                <p>
                  <strong>E. Injunctive Relief</strong><br />
                  Either party shall retain the right to seek injunctive relief in courts of competent jurisdiction for 
                  intellectual property violations or other irreparable harm.
                </p>
                <p>
                  <strong>F. Claims Period</strong><br />
                  All claims shall be brought within one (1) year of the event giving rise to such claim, except where prohibited by law.
                </p>
                
                <h3 className={styles.subSectionTitle}>1.13 International Operations</h3>
                <p>
                  The Service shall maintain operations in multiple jurisdictions. Each User outside the United States shall: 
                  (i) comply with all applicable local laws; (ii) accept data processing in Service-designated jurisdictions; 
                  and (iii) acknowledge potential service limitations based on local regulations. The Service shall comply with 
                  international data transfer requirements including Standard Contractual Clauses where required.
                </p>
                
                <h3 className={styles.subSectionTitle}>1.14 Service Availability</h3>
                <p>
                  The Service shall maintain reasonable uptime for all Site functions. The Service shall provide advance notice 
                  of scheduled maintenance periods. The Service shall implement redundancy measures to minimize service disruptions. 
                  The Service shall not guarantee continuous, uninterrupted access to the Site.
                </p>
                
                <h3 className={styles.subSectionTitle}>1.15 Service Packages and Delivery</h3>
                <p>
                  <strong>A. Package Definitions</strong><br />
                  The Service shall offer three distinct service packages to Users, each designed to meet different needs and requirements. 
                  The Basic Package shall include one custom Musical Work with standard delivery timeframes and MP3 format delivery. 
                  The Premium Package shall include one custom Musical Work with priority delivery, one comprehensive revision request, 
                  choice of professional audio formats, and enhanced support services. The Deluxe Package shall include one custom 
                  Musical Work with optional rush delivery, two comprehensive revision requests, all available audio formats, premium 
                  support services, and eligibility to purchase commercial usage rights.
                </p>
                <p>
                  <strong>B. Delivery Standards</strong><br />
                  The Service shall deliver standard orders within twenty-four (24) to forty-eight (48) hours of order confirmation 
                  and payment verification. For Users selecting rush delivery, the Service shall deliver within three (3) to six (6) 
                  hours of order confirmation and payment verification. The Service shall notify Users promptly of any potential 
                  delays due to high-volume periods, holidays, or technical requirements. The Service shall provide estimated 
                  completion times at the time of order placement.
                </p>
                <p>
                  <strong>C. Revision Rights</strong><br />
                  The Service shall provide revision rights according to each package level. Basic Package Users shall have the 
                  option to purchase individual revisions at the then-current rate. Premium Package Users shall receive one 
                  complimentary revision within the scope of the original order specifications. Deluxe Package Users shall receive 
                  two complimentary revisions within the scope of the original order specifications. The Service shall require all 
                  revision requests to be submitted within thirty (30) days of initial delivery. The Service shall retain sole 
                  discretion in determining whether requested changes fall within the scope of the original order.
                </p>
                <p>
                  <strong>D. Format Options</strong><br />
                  The Service shall deliver all Musical Works in MP3 format at professional-grade 320 kbps quality as the standard 
                  delivery format. Premium and Deluxe Package Users shall have access to additional professional audio formats 
                  including WAV and FLAC upon request at the time of order placement. The Service shall maintain consistent 
                  professional audio quality standards across all format options. The Service shall provide technical specifications 
                  for all available formats upon request.
                </p>
              </section>
              
              <section className={styles.termsSection}>
                <h2>2. ACCESSIBILITY STATEMENT</h2>
                
                <h3 className={styles.subSectionTitle}>2.1 Our Commitment</h3>
                <p>
                  The Service shall be committed to making the Site as accessible as possible to people with disabilities. The Service shall strive to follow WCAG (Web Content Accessibility Guidelines) standards and best practices to ensure a positive experience for all Users, including those who rely on assistive technologies.
                </p>

                <h3 className={styles.subSectionTitle}>2.2 Limitations of Accessibility</h3>
                <p>
                  While the Service shall make best efforts to ensure the accessibility of the Site, the Service shall not warrant or guarantee that:
                </p>
                <ul>
                  <li>The Site and/or Services shall operate and/or be available at all times without disruption or interruption;</li>
                  <li>All content shall be fully accessible to all Users at all times; or</li>
                  <li>The Site shall be immune from unauthorized access or be error-free.</li>
                </ul>
                <p>
                  Despite the Service's ongoing efforts to enhance accessibility, there may be some limitations such as certain pages that are not fully accessible, or situations where appropriate technological solutions have not yet been implemented.
                </p>

                <h3 className={styles.subSectionTitle}>2.3 Reporting Accessibility Issues</h3>
                <p>
                  The Service shall welcome User feedback on the accessibility of the Site. Each User shall be encouraged to contact the Service at support@mail.makemesongs.com if they encounter any accessibility barriers or have suggestions for improvement. The Service shall take all feedback seriously and shall make reasonable efforts to address reported issues.
                </p>

                <h3 className={styles.subSectionTitle}>2.4 Limitation of Liability for Accessibility Issues</h3>
                <p>
                  In no event shall the Service be liable for any damages, including but not limited to:
                </p>
                <ul>
                  <li>Loss of use or access to the Site;</li>
                  <li>Loss of profits or business opportunities; or</li>
                  <li>Special, incidental, consequential, or punitive damages arising from or related to any alleged non-compliance with any disability-related laws or regulations.</li>
                </ul>
                <p>
                  By accessing the Site, each User shall agree to indemnify and hold the Service harmless from and against any claims, damages, or expenses arising from or related to:
                </p>
                <ul>
                  <li>Any alleged failure to comply with disability-related laws;</li>
                  <li>Any claims related to website accessibility; or</li>
                  <li>Any other matter arising from or related to accessibility as covered in these Terms.</li>
                </ul>
                <p>
                  Nothing in these Terms shall be intended to exclude liability which cannot be excluded under applicable law.
                </p>
              </section>
              
              <section className={styles.termsSection}>
                <h2>3. CONTACT INFORMATION</h2>
                
                <h3 className={styles.subSectionTitle}>3.1 Official Channels</h3>
                <p>
                  The Service shall maintain and monitor the following official communication channels:
                </p>
                <p>
                  The Service shall designate support@mail.makemesongs.com as its primary and official electronic mail contact point 
                  for all service-related communications. The Service shall establish and maintain a physical mailing address for 
                  official correspondence once business entity formation is complete. The Service shall publish and maintain current 
                  listings of all official communication channels on the Site. The Service shall monitor all designated channels 
                  during standard business hours, defined as Monday through Friday, 9:00 AM to 5:00 PM Eastern Time, excluding 
                  federal holidays.
                </p>
                
                <h3 className={styles.subSectionTitle}>3.2 Response Commitment</h3>
                <p>
                  The Service shall respond to all privacy-related inquiries within forty-eight (48) hours of receipt.
                </p>
              </section>
            </div>
          ) : activeTab === 'privacy' ? (
            <div className={styles.privacyContent}>
              <p className={styles.lastUpdated}>Last Updated: March 22, 2024</p>
              
              <section className={styles.termsSection}>
                <h2>1. SCOPE AND APPLICABILITY</h2>
                
                <h3 className={styles.subSectionTitle}>1.1 Policy Coverage</h3>
                <p>
                  The Service shall apply this Privacy Policy to all Users globally. The Service shall comply with data protection 
                  requirements in all operating jurisdictions. The Service shall maintain compliance with applicable privacy laws 
                  including without limitation CCPA/CPRA, GDPR, UK GDPR, and PIPEDA.
                </p>
              </section>
              
              <section className={styles.termsSection}>
                <h2>2. INFORMATION COLLECTION AND USAGE</h2>
                
                <h3 className={styles.subSectionTitle}>2.1 User-Provided Information</h3>
                <p>
                  The Service shall collect and process the following categories of User-provided information: (i) account 
                  credentials and contact details necessary for service provision; (ii) song creation content, preferences, and 
                  creative direction provided by Users; (iii) payment information required for transaction processing; and 
                  (iv) support communications and service-related correspondence. The Service shall collect only information 
                  necessary for the stated purposes of service delivery and account management.
                </p>
                
                <h3 className={styles.subSectionTitle}>2.2 Automatically Collected Information</h3>
                <p>
                  The Service shall automatically collect technical data necessary for service operation and improvement, including 
                  without limitation: (i) device identifiers for security and authentication purposes; (ii) browser specifications 
                  to ensure compatibility; (iii) usage patterns to improve service functionality; and (iv) location data when 
                  explicitly permitted by User settings and applicable law. The Service shall provide clear notice of all automatic 
                  data collection practices.
                </p>
                
                <h3 className={styles.subSectionTitle}>2.3 Information Usage Purposes</h3>
                <p>
                  The Service shall use collected information solely for: (i) Musical Work creation and delivery; (ii) payment 
                  processing; (iii) customer support provision; (iv) service improvement; and (v) legal compliance.
                </p>
              </section>
              
              <section className={styles.termsSection}>
                <h2>3. DATA PROCESSING STANDARDS</h2>
                
                <h3 className={styles.subSectionTitle}>3.1 Legal Basis</h3>
                <p>
                  The Service shall process User data solely under the following legal bases: (i) explicit User consent; 
                  (ii) contractual necessity; (iii) legal obligations; and (iv) legitimate business interests. The Service 
                  shall document the specific basis for each processing activity.
                </p>
                
                <h3 className={styles.subSectionTitle}>3.2 Processing Limitations</h3>
                <p>
                  The Service shall process User data only for specified, explicit purposes. The Service shall limit data 
                  collection to information necessary for stated purposes. The Service shall maintain processing records as 
                  required by applicable law.
                </p>
              </section>
              
              <section className={styles.termsSection}>
                <h2>4. INFORMATION SHARING</h2>
                
                <h3 className={styles.subSectionTitle}>4.1 Data Sharing Restrictions</h3>
                <p>
                  The Service shall not sell, rent, or monetize User information under any circumstances. The Service shall 
                  share User information only with: (i) authorized service providers who have executed appropriate data processing 
                  agreements and assist in essential Service operations; (ii) legal authorities when specifically required by 
                  valid legal process or court order; and (iii) successor entities in the event of a business transition, subject 
                  to prior notice to Users when possible. The Service shall maintain detailed records of all data sharing activities.
                </p>
                
                <h3 className={styles.subSectionTitle}>4.2 Service Provider Requirements</h3>
                <p>
                  The Service shall require all third-party service providers to maintain appropriate security measures. The Service 
                  shall enter into data processing agreements with service providers where required by law.
                </p>
              </section>
              
              <section className={styles.termsSection}>
                <h2>5. INTERNATIONAL DATA TRANSFERS</h2>
                
                <h3 className={styles.subSectionTitle}>5.1 Transfer Mechanisms</h3>
                <p>
                  The Service shall transfer User data internationally only with appropriate safeguards in place. The Service shall 
                  implement Standard Contractual Clauses or other approved transfer mechanisms as required by applicable law.
                </p>
                
                <h3 className={styles.subSectionTitle}>5.2 Jurisdictional Compliance</h3>
                <p>
                  The Service shall comply with data protection requirements in all operating jurisdictions. The Service shall 
                  obtain User consent for international transfers where required by law.
                </p>
                
                <h3 className={styles.subSectionTitle}>5.3 Cross-Border Transfers</h3>
                <p>
                  The Service shall implement appropriate safeguards for all international data transfers. The Service shall maintain 
                  compliance with Standard Contractual Clauses where required. The Service shall obtain explicit User consent for 
                  transfers to jurisdictions without adequacy decisions.
                </p>
              </section>
              
              <section className={styles.termsSection}>
                <h2>6. TRACKING TECHNOLOGIES</h2>
                
                <h3 className={styles.subSectionTitle}>6.1 Cookie Usage</h3>
                <p>
                  The Service shall employ essential cookies and similar technologies necessary for basic site functionality and 
                  security. The Service shall clearly disclose all cookie usage in accordance with applicable laws.
                </p>
                
                <h3 className={styles.subSectionTitle}>6.2 User Controls</h3>
                <p>
                  The Service shall comply with applicable cookie laws and regulations in each operating jurisdiction. The Service 
                  shall provide appropriate notices and controls as required by law. The Service shall maintain documentation of 
                  its cookie practices and user consent mechanisms.
                </p>
              </section>
              
              <section className={styles.termsSection}>
                <h2>7. USER PRIVACY RIGHTS</h2>
                
                <h3 className={styles.subSectionTitle}>7.1 California Resident Rights</h3>
                <p>
                  The Service shall provide California residents with the following specific rights under CCPA/CPRA: (i) the right 
                  to request access to their personal data; (ii) the right to request deletion of their personal data; (iii) the right 
                  to opt out of data sharing; and (iv) the right to designate an authorized agent for making requests.
                </p>
                
                <h3 className={styles.subSectionTitle}>7.2 European Union and United Kingdom Rights</h3>
                <p>
                  The Service shall provide EU and UK residents with the following rights under GDPR and UK GDPR: (i) the right to 
                  access and update personal data; (ii) the right to request deletion of personal data; (iii) the right to withdraw 
                  processing consent; (iv) the right to data portability; and (v) the right to file complaints with local Data 
                  Protection Authorities.
                </p>
                
                <h3 className={styles.subSectionTitle}>7.3 Rights Exercise Process</h3>
                <p>
                  The Service shall require all rights requests to be submitted to support@mail.makemesongs.com. The Service shall 
                  verify the identity of requesting Users through email confirmation or other lawful methods before processing rights 
                  requests. The Service shall respond to rights requests within the timeframes required by applicable law.
                </p>
                
                <h3 className={styles.subSectionTitle}>7.4 Data Retention</h3>
                <p>
                  The Service shall retain User data only for the duration necessary to fulfill legal and business obligations. 
                  The Service shall process data deletion requests in accordance with applicable retention requirements.
                </p>
              </section>
              
              <section className={styles.termsSection}>
                <h2>8. CHILDREN'S PRIVACY</h2>
                
                <h3 className={styles.subSectionTitle}>8.1 Age Restrictions</h3>
                <p>
                  The Service shall not knowingly collect or process personal information from individuals under thirteen (13) 
                  years of age in the United States or under sixteen (16) years of age in the European Union. The Service shall 
                  promptly delete any information discovered to belong to underage individuals.
                </p>
                
                <h3 className={styles.subSectionTitle}>8.2 Parental Rights</h3>
                <p>
                  The Service shall respond to all parental requests regarding children's data within forty-eight (48) hours. 
                  The Service shall provide parents with the ability to review, delete, or prevent further collection of their 
                  child's information.
                </p>
              </section>
              
              <section className={styles.termsSection}>
                <h2>9. SECURITY MEASURES</h2>
                
                <h3 className={styles.subSectionTitle}>9.1 Technical Safeguards</h3>
                <p>
                  The Service shall implement industry-standard security measures to protect User information. The Service shall 
                  maintain appropriate technical safeguards including without limitation encryption protocols, secure socket layer 
                  technology, and firewall protection.
                </p>
                
                <h3 className={styles.subSectionTitle}>9.2 Security Limitations</h3>
                <p>
                  The Service shall acknowledge that no method of electronic storage or transmission is one hundred percent (100%) 
                  secure. The Service shall not guarantee absolute security but shall maintain reasonable security measures 
                  appropriate to the nature of the information.
                </p>
                
                <h3 className={styles.subSectionTitle}>9.3 Security Monitoring</h3>
                <p>
                  The Service shall conduct continuous monitoring of all systems and networks. The Service shall perform regular 
                  security audits and vulnerability assessments. The Service shall maintain incident response procedures for 
                  potential security events.
                </p>
                
                <h3 className={styles.subSectionTitle}>9.4 Employee Access Controls</h3>
                <p>
                  The Service shall restrict data access to authorized personnel only. The Service shall require multi-factor 
                  authentication for all system access. The Service shall maintain detailed access logs for all data interactions.
                </p>
                
                <h3 className={styles.subSectionTitle}>9.5 Breach Response Protocol</h3>
                <p>
                  The Service shall maintain a documented incident response plan. The Service shall notify affected Users of any 
                  data breaches within seventy-two (72) hours. The Service shall cooperate with law enforcement and regulatory 
                  authorities during breach investigations. The Service shall conduct post-incident reviews to prevent future 
                  occurrences.
                </p>
              </section>
              
              <section className={styles.termsSection}>
                <h2>10. POLICY UPDATES</h2>
                
                <h3 className={styles.subSectionTitle}>10.1 Modification Rights</h3>
                <p>
                  The Service shall retain the right to update this Privacy Policy at any time. The Service shall indicate all 
                  updates through the Last Updated date at the beginning of this Policy.
                </p>
                
                <h3 className={styles.subSectionTitle}>10.2 Notice of Changes</h3>
                <p>
                  The Service shall notify Users of material changes to this Policy through the Site or via email. The Service shall 
                  provide Users with the opportunity to review updated terms before continued use of the Service.
                </p>
                
                <h3 className={styles.subSectionTitle}>10.3 Compliance Auditing</h3>
                <p>
                  The Service shall conduct regular privacy compliance audits. The Service shall maintain documentation of all 
                  privacy-related procedures. The Service shall update privacy practices based on audit findings. The Service 
                  shall retain independent auditors when required by law.
                </p>
                
                <h3 className={styles.subSectionTitle}>10.4 Training Requirements</h3>
                <p>
                  The Service shall provide privacy training to all personnel with data access. The Service shall maintain current 
                  training materials and records. The Service shall require periodic recertification of privacy knowledge. The 
                  Service shall update training content based on regulatory changes.
                </p>
              </section>
              
              <section className={styles.termsSection}>
                <h2>11. CONTACT INFORMATION</h2>
                
                <h3 className={styles.subSectionTitle}>11.1 Official Channels</h3>
                <p>
                  The Service shall maintain and monitor the following official communication channels:
                  The Service shall designate support@mail.makemesongs.com as its primary and official electronic mail contact point 
                  for all service-related communications. The Service shall establish and maintain a physical mailing address for 
                  official correspondence once business entity formation is complete. The Service shall publish and maintain current 
                  listings of all official communication channels on the Site. The Service shall monitor all designated channels 
                  during standard business hours, defined as Monday through Friday, 9:00 AM to 5:00 PM Eastern Time, excluding 
                  federal holidays.
                </p>
                
                <h3 className={styles.subSectionTitle}>11.2 Response Commitment</h3>
                <p>
                  The Service shall respond to all privacy-related inquiries within forty-eight (48) hours of receipt.
                </p>
                </section>
            </div>
          ) : (
            <div className={styles.refundContent}>
              <p className={styles.lastUpdated}>Last Updated: March 22, 2024</p>
              
              <section className={styles.termsSection}>
                <h2>1. CUSTOM NATURE OF SERVICE</h2>
                
                <h3 className={styles.subSectionTitle}>1.1 Product Characteristics</h3>
                <p>
                  The Service shall create each Musical Work as a unique, custom-made digital product based on User specifications. 
                  Due to the personalized nature of each Musical Work and the immediate commencement of creative and production 
                  work upon order placement, all sales shall be final once the order is confirmed. The Service shall begin allocation 
                  of creative resources immediately upon receiving payment. The Service shall consider extremely limited exceptions 
                  only as specifically set forth in Section 2.1 of this Policy.
                </p>
                
                <h3 className={styles.subSectionTitle}>1.2 Review Requirements</h3>
                <p>
                  Each User shall review their delivered Musical Work within forty-eight (48) hours of receipt. Each User shall 
                  submit any concerns to support@mail.makemesongs.com within this review period. The Service shall deem the 
                  Musical Work accepted if no response is received within forty-eight (48) hours of delivery.
                </p>
                
                <h3 className={styles.subSectionTitle}>1.3 Delivery Standards</h3>
                <p>
                  The Service shall deliver all Musical Works within twenty-four (24) to forty-eight (48) hours of order confirmation 
                  for standard delivery, and within three (3) to six (6) hours for rush delivery orders. The Service shall deliver 
                  all works in industry-standard digital formats. The Service shall maintain backup copies for thirty (30) days 
                  post-delivery. The Service shall provide download links that remain active for seventy-two (72) hours after 
                  initial delivery.
                </p>
              </section>
              
              <section className={styles.termsSection}>
                <h2>2. REFUND ELIGIBILITY</h2>
                
                <h3 className={styles.subSectionTitle}>2.1 Qualifying Circumstances</h3>
                <p>
                  The Service shall consider refund requests only under the following limited circumstances: (i) when the Service 
                  determines, prior to beginning production, that it cannot complete an order to specifications; (ii) when submitted 
                  content violates Service policies necessitating immediate order cancellation; (iii) when the User fails to provide 
                  sufficient information before production begins and declines to provide additional details after reasonable 
                  attempts by the Service; or (iv) when verified technical delivery failures occur that the Service cannot resolve 
                  within seventy-two (72) hours of initial attempt. The Service shall not issue refunds once production of the 
                  Musical Work has commenced.
                </p>
                
                <h3 className={styles.subSectionTitle}>2.2 Discretionary Nature</h3>
                <p>
                  The Service shall retain sole and absolute discretion in determining refund eligibility. The Service shall evaluate 
                  each refund request based on documented circumstances, order history, compliance with these Terms, and production 
                  status. The Service shall not be obligated to issue refunds for any reason once work has begun on a Musical Work. 
                  The Service shall maintain detailed records of all refund determinations and the specific grounds for each decision.
                </p>
              </section>
              
              <section className={styles.termsSection}>
                <h2>3. RESOLUTION PROCESS</h2>
                
                <h3 className={styles.subSectionTitle}>3.1 Resolution Options</h3>
                <p>
                  The Service shall address User concerns through the following structured resolution process: (i) revisions strictly 
                  within the scope of the original order specifications and package limitations, provided such revisions are included 
                  in the User's package; (ii) detailed written explanations of creative and technical decisions; (iii) store credits 
                  only in rare and exceptional circumstances as solely determined by the Service; and (iv) refunds only in the limited 
                  circumstances explicitly defined in Section 2.1 and only before production resources have been allocated. The Service 
                  shall not consider refund requests based on subjective creative preferences, changes in User requirements, or after 
                  any work has commenced.
                </p>
                
                <h3 className={styles.subSectionTitle}>3.2 Resolution Timeline</h3>
                <p>
                  The Service shall acknowledge all concerns within twenty-four (24) hours of receipt. The Service shall provide 
                  initial resolution options within two (2) business days. The Service shall implement approved revisions within 
                  the standard delivery timeframe of twenty-four (24) to forty-eight (48) hours.
                </p>
                
                <h3 className={styles.subSectionTitle}>3.3 Non-Refundable Circumstances</h3>
                <p>
                  The Service shall not issue refunds in the following circumstances: (i) when production of the Musical Work has 
                  begun; (ii) when the User has approved any portion of the work; (iii) when the User's creative preferences change 
                  after order placement; (iv) when the User fails to respond to Service communications within specified timeframes; 
                  (v) when the User has exhausted included revision attempts; or (vi) when the User's expectations fall outside the 
                  scope of the ordered package.
                </p>
                
                <h3 className={styles.subSectionTitle}>3.4 Production Commencement</h3>
                <p>
                  The Service shall be deemed to have commenced work immediately upon order confirmation and payment processing. 
                  The Service shall consider the allocation of any resources, including but not limited to creative planning, 
                  musical arrangement, or production preparation, as commencement of work. The Service shall not be obligated 
                  to provide refunds once any such work has begun, regardless of the stage of completion.
                </p>
              </section>
              
              <section className={styles.termsSection}>
                <h2>4. STORE CREDIT TERMS</h2>
                
                <h3 className={styles.subSectionTitle}>4.1 Credit Validity</h3>
                <p>
                  The Service shall issue store credits, at its sole discretion, with a fixed validity period of twelve (12) months 
                  from the date of issuance. Store credits shall be considered a courtesy accommodation and not an entitlement.
                </p>
                
                <h3 className={styles.subSectionTitle}>4.2 Credit Usage</h3>
                <p>
                  The Service shall apply store credits only to future purchases within the specified validity period. The Service 
                  shall not permit the combination of store credits with any other discounts, promotions, or special offers. 
                  The Service shall require that store credits be used in their entirety in a single transaction.
                </p>
                
                <h3 className={styles.subSectionTitle}>4.3 Credit Documentation</h3>
                <p>
                  The Service shall maintain comprehensive records of all store credit transactions, including issuance date, 
                  amount, usage, and expiration date. The Service shall provide Users with written confirmation of credit balances 
                  within twenty-four (24) hours of request. The Service shall automatically notify Users of pending credit expiration 
                  thirty (30) days and seven (7) days prior to expiration date.
                </p>
              </section>
              
              <section className={styles.termsSection}>
                <h2>5. CHARGEBACK PROVISIONS</h2>
                
                <h3 className={styles.subSectionTitle}>5.1 Chargeback Restrictions</h3>
                <p>
                  The Service shall require Users to contact support@mail.makemesongs.com and fully exhaust all available resolution 
                  options prior to initiating any payment chargebacks, credit card disputes, or payment reversals of any kind. 
                  The Service shall require Users to explicitly acknowledge at checkout that: (i) unauthorized chargebacks or credit 
                  card disputes constitute a breach of these Terms; (ii) the Service retains the right to pursue debt collection; 
                  (iii) the Service may report unauthorized disputes to credit bureaus; and (iv) the User may be liable for additional 
                  fees, including but not limited to chargeback fees, collection costs, and legal expenses. The Service shall maintain 
                  comprehensive documentation of all communications, delivery confirmations, and work products to defend against 
                  unauthorized chargebacks.
                </p>
                
                <h3 className={styles.subSectionTitle}>5.2 Dispute Resolution</h3>
                <p>
                  The Service shall maintain comprehensive records of all payment transactions, delivery confirmations, User 
                  communications, and explicit acknowledgments of these Terms. The Service shall document User acceptance of 
                  the non-refundable nature of custom digital goods. The Service shall retain evidence of: (i) User's agreement 
                  to these Terms; (ii) timestamps of work commencement; (iii) all service delivery milestones; and (iv) any 
                  attempted resolution prior to disputes. The Service shall use such documentation to defend against credit card 
                  disputes and unauthorized payment reversals.
                </p>
                
                <h3 className={styles.subSectionTitle}>5.3 Payment Reversals</h3>
                <p>
                  The Service shall investigate all payment reversals and chargebacks immediately upon notification. The Service 
                  shall maintain complete documentation of all delivery confirmations, User communications, service provisions, 
                  and resolution attempts. The Service shall submit comprehensive evidence packages to payment processors within 
                  forty-eight (48) hours of receiving chargeback notifications, including but not limited to: (i) User's explicit 
                  acknowledgment of non-refundable terms; (ii) proof of service delivery; (iii) all communication attempts; and 
                  (iv) documentation of work performed. The Service shall track the outcome of all payment disputes and maintain 
                  records for future fraud prevention.
                </p>
                
                <h3 className={styles.subSectionTitle}>5.4 Digital Goods Protection</h3>
                <p>
                  The Service shall implement the following protective measures for digital goods transactions:
                </p>
                <p>
                  <strong>A. Order Acknowledgment</strong><br />
                  The Service shall require Users to explicitly acknowledge at checkout that they are: (i) ordering a non-refundable, 
                  custom-created digital product; (ii) agreeing that work begins immediately upon payment; and (iii) understanding 
                  that the nature of digital goods prevents returns once delivered.
                </p>
                <p>
                  <strong>B. Payment Processor Documentation</strong><br />
                  The Service shall maintain detailed transaction records specifically formatted for digital goods disputes, 
                  including: (i) IP address logs; (ii) download timestamps; (iii) delivery confirmations; and (iv) User activity 
                  logs showing access to the delivered works.
                </p>
                <p>
                  <strong>C. Dispute Prevention Notices</strong><br />
                  The Service shall display clear notices throughout the ordering process that: (i) all sales are final; (ii) work 
                  begins immediately; (iii) digital goods cannot be returned; and (iv) unauthorized payment disputes will result 
                  in legal action.
                </p>
              </section>
              
              <section className={styles.termsSection}>
                <h2>6. COMMUNICATION CHANNELS</h2>
                
                <h3 className={styles.subSectionTitle}>6.1 Communication Standards</h3>
                <p>
                  The Service shall designate support@mail.makemesongs.com as its primary and official electronic mail contact 
                  point for all service-related communications. The Service shall establish and maintain a physical mailing address 
                  for official correspondence once business entity formation is complete. The Service shall publish and maintain 
                  current listings of all official communication channels on the Site. The Service shall monitor all designated 
                  channels during standard business hours, defined as Monday through Friday, 9:00 AM to 5:00 PM Eastern Time, 
                  excluding federal holidays.
                </p>
                
                <h3 className={styles.subSectionTitle}>6.2 Response Requirements</h3>
                <p>
                  The Service shall acknowledge receipt of all inquiries within twenty-four (24) hours of receipt during business hours. 
                  The Service shall provide substantive responses to refund-related matters within two (2) business days of initial 
                  acknowledgment. The Service shall maintain detailed records of all communications, including timestamps, content, 
                  and resolution outcomes. The Service shall implement an escalation protocol for urgent matters requiring expedited 
                  review. The Service shall retain sole discretion in determining matters qualifying for expedited handling.
                </p>
                
                <h3 className={styles.subSectionTitle}>6.3 Refund Processing</h3>
                <p>
                  The Service shall process approved refunds within five (5) business days of approval. The Service shall issue refunds 
                  through the original payment method where technically and legally feasible. The Service shall document all refund 
                  transactions with unique identifiers and maintain comprehensive transaction records. The Service shall provide 
                  written confirmation of all processed refunds to the User's registered email address. The Service shall maintain 
                  records of all refund-related communications and processing documentation for a minimum of two (2) years.
                </p>
              </section>
              
              <section className={styles.termsSection}>
                <h2>7. REFUND DOCUMENTATION</h2>
                
                <h3 className={styles.subSectionTitle}>7.1 Record Maintenance</h3>
                <p>
                  The Service shall maintain complete records of all refund requests and resolutions. The Service shall document 
                  the specific basis for each refund decision. The Service shall preserve all related communications for a minimum 
                  of two (2) years.
                </p>
                
                <h3 className={styles.subSectionTitle}>7.2 Reporting Requirements</h3>
                <p>
                  The Service shall generate monthly reports of all refund activity. The Service shall analyze refund patterns to 
                  improve service quality. The Service shall maintain statistical data regarding refund rates and resolution times.
                </p>
              </section>
              
              <section className={styles.termsSection}>
                <h2>8. QUALITY ASSURANCE</h2>
                
                <h3 className={styles.subSectionTitle}>8.1 Review Standards</h3>
                <p>
                  The Service shall maintain comprehensive quality control measures throughout the creation process of all Musical Works. 
                  The Service shall conduct multi-point technical reviews of audio quality, mix balance, and format specifications prior 
                  to delivery. The Service shall verify compliance with User-provided specifications and creative direction before release. 
                  The Service shall document all quality control checks performed on each Musical Work.
                </p>
                
                <h3 className={styles.subSectionTitle}>8.2 Performance Monitoring</h3>
                <p>
                  The Service shall maintain detailed tracking systems for delivery times, revision requests, and completion rates. 
                  The Service shall monitor and analyze User satisfaction metrics across all service aspects. The Service shall 
                  implement data-driven improvement protocols based on performance analytics. The Service shall generate regular 
                  reports on quality metrics to identify areas for service enhancement.
                </p>
                
                <h3 className={styles.subSectionTitle}>8.3 Technical Standards</h3>
                <p>
                  The Service shall maintain industry-standard audio quality benchmarks for all Musical Works. The Service shall 
                  conduct regular calibration of production systems and tools. The Service shall verify audio quality across 
                  multiple playback systems before delivery. The Service shall maintain documentation of all technical specifications 
                  and quality standards.
                </p>
              </section>
              
              <section className={styles.termsSection}>
                <h2>9. DISPUTE PREVENTION AND RESOLUTION</h2>
                
                <h3 className={styles.subSectionTitle}>9.1 Preventive Measures</h3>
                <p>
                  The Service shall implement clear communication protocols throughout the creation process. The Service shall 
                  provide detailed progress updates to Users. The Service shall identify potential issues before delivery. 
                  The Service shall maintain proactive customer support standards.
                </p>
                
                <h3 className={styles.subSectionTitle}>9.2 Mediation Process</h3>
                <p>
                  The Service shall offer mediation services for complex disputes. The Service shall designate qualified personnel 
                  for dispute resolution. The Service shall maintain impartial review procedures. The Service shall document all 
                  mediation proceedings.
                </p>
              </section>
              
              <section className={styles.termsSection}>
                <h2>10. SERVICE IMPROVEMENTS</h2>
                
                <h3 className={styles.subSectionTitle}>10.1 Feedback Integration</h3>
                <p>
                  The Service shall analyze all refund requests for service improvement opportunities. The Service shall maintain 
                  a systematic process for implementing feedback-based changes. The Service shall track the effectiveness of 
                  implemented improvements.
                </p>
                
                <h3 className={styles.subSectionTitle}>10.2 Quality Metrics</h3>
                <p>
                  The Service shall establish and monitor key performance indicators including without limitation: (i) refund 
                  request rates; (ii) resolution timeframes; (iii) customer satisfaction scores; and (iv) repeat purchase rates.
                </p>
              </section>
              
              <section className={styles.termsSection}>
                <h2>11. INTERNATIONAL REFUND COMPLIANCE</h2>
                
                <h3 className={styles.subSectionTitle}>11.1 Currency Handling</h3>
                <p>
                  The Service shall process refunds in the original transaction currency. The Service shall apply current exchange 
                  rates for cross-border refunds. The Service shall absorb any currency conversion fees.
                </p>
                
                <h3 className={styles.subSectionTitle}>11.2 Regional Requirements</h3>
                <p>
                  The Service shall comply with local refund regulations in all operating jurisdictions. The Service shall maintain 
                  documentation of jurisdiction-specific refund requirements. The Service shall update refund procedures based on 
                  regulatory changes.
                </p>
              </section>
              
              <section className={styles.termsSection}>
                <h2>12. TECHNICAL REQUIREMENTS</h2>
                
                <h3 className={styles.subSectionTitle}>12.1 System Compatibility</h3>
                <p>
                  The Service shall maintain compatibility with current industry-standard audio formats. The Service shall provide 
                  technical specifications for optimal playback. The Service shall assist Users with basic technical support for 
                  accessing delivered works.
                </p>
                
                <h3 className={styles.subSectionTitle}>12.2 Download Procedures</h3>
                <p>
                  The Service shall provide secure download links via email. The Service shall implement appropriate security 
                  measures for file transfers. The Service shall offer alternative delivery methods if technical issues arise.
                </p>
              </section>
              
              <section className={styles.termsSection}>
                <h2>13. TERMINATION</h2>
                
                <h3 className={styles.subSectionTitle}>13.1 Termination Rights</h3>
                <p>
                  The Service shall retain the right to terminate or suspend access to the Service at any time for violations 
                  of these Terms. The Service shall provide notice of termination where feasible. The Service shall process any 
                  pending refunds prior to account closure.
                </p>
                
                <h3 className={styles.subSectionTitle}>13.2 Post-Termination</h3>
                <p>
                  The Service shall maintain User data in accordance with legal requirements post-termination. The Service shall 
                  honor existing licenses for delivered Musical Works. The Service shall cease processing any pending orders 
                  upon termination.
                </p>
              </section>
              
              <section className={styles.termsSection}>
                <h2>14. FORCE MAJEURE</h2>
                
                <h3 className={styles.subSectionTitle}>14.1 Force Majeure Events</h3>
                <p>
                  The Service shall be excused from performance for any period during which, and to the extent that, it is 
                  prevented from performing any obligation or service by: (i) acts of God; (ii) fire; (iii) war; (iv) civil unrest; 
                  (v) government action; (vi) internet disruption; (vii) power failure; (viii) equipment failure; or (ix) other 
                  causes beyond its reasonable control.
                </p>
                
                <h3 className={styles.subSectionTitle}>14.2 Notification Requirements</h3>
                <p>
                  The Service shall provide prompt notice of any force majeure event. The Service shall resume performance as 
                  soon as reasonably possible. The Service shall maintain contingency plans for service continuity where feasible.
                </p>
              </section>
              
              <section className={styles.termsSection}>
                <h2>15. SEVERABILITY</h2>
                
                <h3 className={styles.subSectionTitle}>15.1 Severability Clause</h3>
                <p>
                  If any provision of these Terms shall be deemed unlawful, void, or unenforceable, such provision shall be 
                  deemed severable from these Terms and shall not affect the validity and enforceability of any remaining provisions. 
                  The Service shall replace any severed provision with a valid provision that most closely matches the intent 
                  of the original provision.
                </p>
              </section>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TermsPrivacyPage;